import { useNavigate, useParams } from "react-router-dom";
import { StyledSearchBar } from "../../../../../../../../components/common";
import { useTranslation } from "react-i18next";
import { routes } from "../../../../../../../../navigation/routes";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../store/config";
import { Button, notification, Spin, Pagination } from "antd";
import { useEffect, useState, useMemo } from "react";
import { IMAGE_BASE_URL } from "../../../../../../../../common/config";
import { useDebounce } from "../../../../../../../../hooks/useDebounce";
import { ReactComponent as PlusIcon } from "../../../../../../../../assets/icons/WhitePlus.svg";
import DeleteModal from "../../../../../../../../components/common/DeleteModal";
import { SortType } from "../../../../../../../../interfaces/screens/fetch-params-types";
import { NewWorkoutModal } from "../../../workouts/NewWorkoutModal";
import {
  StatusTag,
  StyledFiltersBar,
  StyledFilterButton,
  StyledSortIcon,
} from "../../../../../../../../components/common/styles";
import {
  getTrainingProgramStart,
  overrideWorkoutInTrainingProgramStart,
  removeWorkoutFromProgramStart,
  resetTrainingProgramError,
  toggleEnableWorkoutInTrainingProgramStart,
  updateWorkoutsTableState,
} from "../../../../../../../../store/features/training-program/training-program-slice";
import { MoveDayAndWeekModal } from "../../../workouts/MoveDayAndWeekModal";
import {
  StyledExtendedCellContent,
  StyledExtendedCellImage,
  StyledExtendedCellTitle,
  StyledExtendedTableCell,
  StyledTable,
  StyledTableActionsButton,
  StyledThreeDotsVertical,
} from "../../../../../../../../components/common/styles/tableStyles";
import { DuplicateWorkoutModal } from "../../../workouts/DuplicateWorkoutModal";
import { StyledDropdown } from "../../../../../header/style";

type ModifiedWorkout = {
  name: string;
  image: string;
  enabled: boolean;
  day: number | undefined;
  week: number | undefined;
  _id: string;
  updatedAt?: string;
};

export const TrainingProgramWorkoutsTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { id: trainerId } = useParams<{ id: string }>();
  const { trainingProgramId } = useParams<{ trainingProgramId: string }>();

  const [openMoveDayModal, setOpenMoveDayModal] = useState<boolean>(false);
  const [moveDayModalError, setMoveDayModalError] = useState<string>("");
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);
  const [duplicateModalError, setDuplicateModalError] = useState<string>("");
  const [currentTrainingProgramWorkoutId, setCurrentTrainingProgramWorkoutId] =
    useState<string>("");
  const [workouts, setWorkouts] = useState<ModifiedWorkout[]>([]);

  const {
    trainingProgram,
    loading,
    error,
    requestState,
    operation,
    workoutsTableState,
  } = useAppSelector((state) => state.trainingProgram);

  const { search, sort, pagination } = workoutsTableState;
  const debouncedSearch = useDebounce(search, 300);

  const handleSearchChange = (value: string) => {
    dispatch(
      updateWorkoutsTableState({
        ...workoutsTableState,
        search: value,
        pagination: { ...pagination, page: 0 },
      })
    );
  };

  const handleSort = (sortBy: string, sortOrder: number) => {
    const currSortOrder = sortOrder as 1 | -1;

    dispatch(
      updateWorkoutsTableState({
        ...workoutsTableState,
        sort: { sortBy, sortOrder: currSortOrder },
        pagination: { ...pagination, page: 0 },
      })
    );
  };

  const handlePaginationChange = (page: number) => {
    dispatch(
      updateWorkoutsTableState({
        ...workoutsTableState,
        pagination: { ...pagination, page: page - 1 },
      })
    );
  };

  useEffect(() => {
    if (trainingProgram?.workouts) {
      const workouts = trainingProgram.workouts.map((workout, index) => {
        const day = index % trainingProgram.timesPerWeek;
        const week = (index - day) / trainingProgram.timesPerWeek;

        if (workout)
          return {
            _id: workout._id,
            name: workout.name,
            image: workout.outerCover,
            enabled: workout.enabled,
            day,
            week,
            updatedAt: workout.updatedAt,
          };
      });

      const filteredWorkouts = workouts.filter(
        (workout) => workout !== undefined
      );

      const inactiveWorkouts =
        trainingProgram.backlog?.map((workout) => {
          return {
            _id: workout._id,
            name: workout.name,
            image: workout.outerCover,
            enabled: workout.enabled,
            day: undefined,
            week: undefined,
            updatedAt: workout.updatedAt,
          };
        }) || [];

      setWorkouts([
        ...filteredWorkouts,
        ...inactiveWorkouts,
      ] as ModifiedWorkout[]);
    }
  }, [trainingProgram]);

  useEffect(() => {
    if (error) {
      const specificError =
        error.message === "Index already occupied in training program"
          ? t("Cannot schedule on this day as there is already a workout")
          : undefined;
      if (specificError) {
        setMoveDayModalError(specificError);
        setDuplicateModalError(specificError);
      } else {
        openNotification(specificError);
      }

      dispatch(resetTrainingProgramError());
    }
  }, [error]);

  useEffect(() => {
    if (!openDeleteModal && !openMoveDayModal && !openDuplicateModal) {
      setCurrentTrainingProgramWorkoutId("");
      setMoveDayModalError("");
      setDuplicateModalError("");
    }
  }, [openDeleteModal, openMoveDayModal]);

  useEffect(() => {
    if (requestState === "finish" && operation === "update") {
      setOpenDeleteModal(false);
      setOpenMoveDayModal(false);
      setOpenDuplicateModal(false);
      trainingProgramId &&
        dispatch(getTrainingProgramStart({ trainingProgramId }));
    }
  }, [requestState, operation]);

  const processedWorkouts = useMemo(() => {
    let filteredData = [...workouts];
    if (debouncedSearch) {
      filteredData = filteredData.filter((workout) =>
        workout.name.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    if (sort.sortBy) {
      filteredData.sort((a, b) => {
        const key = sort.sortBy as keyof ModifiedWorkout;
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.sortOrder * aValue.localeCompare(bValue);
        }

        if (aValue < bValue) return -1 * sort.sortOrder;
        if (aValue > bValue) return 1 * sort.sortOrder;
        return 0;
      });
    }

    return {
      data: filteredData,
      total: filteredData.length,
    };
  }, [workouts, debouncedSearch, sort]);

  const paginatedWorkouts = useMemo(() => {
    const start = pagination.page * pagination.limit;
    const end = start + pagination.limit;
    return processedWorkouts.data.slice(start, end);
  }, [processedWorkouts, pagination]);

  const openNotification = (error?: string) => {
    api["error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const handleToggleEnableWorkout = (workoutId: string, enable: boolean) => {
    if (enable) {
      setCurrentTrainingProgramWorkoutId(workoutId);
      setOpenMoveDayModal(true);
    } else {
      toggleEnable(workoutId, enable);
    }
  };

  const toggleEnable = (
    workoutId: string,
    enable: boolean,
    day?: string | number,
    week?: string | number
  ) => {
    let index = undefined;
    if (week !== undefined && day !== undefined && trainingProgram) {
      index =
        parseInt(week + "") * trainingProgram.timesPerWeek + parseInt(day + "");
    }

    if (trainingProgramId) {
      dispatch(
        toggleEnableWorkoutInTrainingProgramStart({
          trainingProgramId,
          workoutId,
          enable,
          index,
        })
      );
    }
  };

  const overrideWorkout = (
    workoutId: string,
    day?: string | number,
    week?: string | number
  ) => {
    let index = undefined;
    if (week !== undefined && day !== undefined && trainingProgram) {
      index =
        parseInt(week + "") * trainingProgram.timesPerWeek + parseInt(day + "");
    }
    if (index !== undefined) {
      const workoutToOverride = trainingProgram?.workouts[index];
      if (workoutToOverride && trainingProgramId) {
        dispatch(
          overrideWorkoutInTrainingProgramStart({
            trainingProgramId,
            workoutId,
            workoutToOverrideId: workoutToOverride._id || workoutToOverride,
            index,
          })
        );
      }
    }
  };

  const columns = [
    {
      title: t("Workout Name"),
      dataIndex: "name",
      key: "name",
      render: (_: any, record: any) => {
        return (
          <StyledExtendedTableCell>
            <StyledExtendedCellImage
              src={IMAGE_BASE_URL + "/" + record.image}
            />
            <StyledExtendedCellContent>
              <StyledExtendedCellTitle>{record.name}</StyledExtendedCellTitle>
            </StyledExtendedCellContent>
          </StyledExtendedTableCell>
        );
      },
    },
    {
      title: t("Week In Program"),
      dataIndex: "week",
      key: "week",
      render: (week: number | undefined) => (
        <>{week === undefined ? "-" : week + 1}</>
      ),
    },
    {
      title: t("Day In Program"),
      dataIndex: "day",
      key: "day",
      render: (day: number | undefined) => (
        <>{day === undefined ? "-" : day + 1}</>
      ),
    },
    {
      title: t("Workout Status"),
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled: boolean) => (
        <StatusTag active={enabled}>
          {t(enabled ? "Active" : "Inactive")}
        </StatusTag>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_: any, record: any) => {
        const actionsMenuItems = [
          {
            key: "edit",
            label: t("Edit"),
            onClick: (record: any) =>
              navigate(
                `${routes.appRoutes.trainers}/${trainerId}/training-programs/${trainingProgramId}/workouts/${record._id}`,
                {
                  state: { isEdit: true },
                }
              ),
          },
          {
            key: "updateStatus",
            label:
              t("Change Status To-") +
              " " +
              (record.enabled ? t("Inactive") : t("Active")),
            onClick: () => {
              handleToggleEnableWorkout(record._id, !record.enabled);
            },
          },
          {
            key: "duplicate",
            label: t("Duplicate Workout"),
            onClick: () => {
              setCurrentTrainingProgramWorkoutId(record._id);
              setOpenDuplicateModal(true);
            },
          },
          {
            key: "delete",
            label: t("Delete"),
            onClick: () => {
              setCurrentTrainingProgramWorkoutId(record._id);
              setOpenDeleteModal(true);
            },
          },
        ];
        return (
          <StyledTableActionsButton
            menu={{
              items: actionsMenuItems.map((item) => ({
                key: item.key,
                label: item.label,
                onClick: () => item.onClick(record),
              })),
            }}
            trigger={["click"]}
          >
            <StyledThreeDotsVertical />
          </StyledTableActionsButton>
        );
      },
    },
  ];

  const handleDelete = () => {
    if (trainingProgramId && currentTrainingProgramWorkoutId) {
      dispatch(
        removeWorkoutFromProgramStart({
          workoutId: currentTrainingProgramWorkoutId,
          trainingProgramId,
        })
      );
    } else {
      openNotification();
    }
  };

  return (
    <>
      {contextHolder}
      <TrainingProgramWorkoutsTableHeader
        trainerId={trainerId}
        trainingProgramId={trainingProgramId}
        search={search}
        setSearch={handleSearchChange}
        sort={sort}
        setSort={handleSort}
      />
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={paginatedWorkouts}
          pagination={false}
          rowKey="_id"
          locale={{ emptyText: t("No results") }}
        />
        <Pagination
          current={pagination.page + 1}
          pageSize={pagination.limit}
          total={processedWorkouts.total}
          onChange={handlePaginationChange}
          showSizeChanger={false}
          hideOnSinglePage
          style={{
            direction: "ltr",
            marginTop: "60px",
            justifyContent: "center",
          }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={loading}
        />
        <DuplicateWorkoutModal
          error={duplicateModalError}
          modalOpen={openDuplicateModal}
          timesPerWeek={trainingProgram?.timesPerWeek || 0}
          currentWorkoutId={currentTrainingProgramWorkoutId}
          setModalOpen={setOpenDuplicateModal}
          setError={setDuplicateModalError}
        />
        <MoveDayAndWeekModal
          error={moveDayModalError}
          modalOpen={openMoveDayModal}
          handleModalOverride={(day, week) => {
            if (day !== null && week !== null) {
              overrideWorkout(currentTrainingProgramWorkoutId, day, week);
            }
          }}
          setModalOpen={setOpenMoveDayModal}
          numberOfWeeks={trainingProgram?.numberOfWeeks || 0}
          timesPerWeek={trainingProgram?.timesPerWeek || 0}
          setError={setMoveDayModalError}
          handleModalOk={(day, week) => {
            if (day !== null && week !== null) {
              toggleEnable(currentTrainingProgramWorkoutId, true, day, week);
            }
          }}
        />
      </Spin>
    </>
  );
};

export const TrainingProgramWorkoutsTableHeader = (props: {
  trainerId: string | undefined;
  trainingProgramId: string | undefined;
  search: string;
  setSearch: (search: string) => void;
  sort: SortType;
  setSort: (sortBy: string, sortOrder: number) => void;
}) => {
  const { t } = useTranslation();
  const { trainerId, trainingProgramId, search, setSearch, sort, setSort } =
    props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleCreateNewWorkout = () => {
    setModalOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSortClick = (e: any) => {
    setSort(e.key, e.key === "updatedAt" ? -1 : 1);
  };

  const sortByOptions = [
    {
      key: "name",
      label: t("Sort by A-Z"),
    },
    {
      key: "updatedAt",
      label: t("Sort by Last Update"),
    },
    {
      key: "enabled",
      label: t("Sort by Status"),
    },
  ];

  const currentSortBy = sort.sortBy;

  return (
    <>
      <StyledFiltersBar>
        <Button
          type="primary"
          shape="round"
          onClick={handleCreateNewWorkout}
          icon={<PlusIcon width={8} height={8} />}
          iconPosition="end"
        >
          {t("Create New Workout")}
        </Button>
        <NewWorkoutModal
          trainerId={trainerId}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          navigateTo={`/trainers/${trainerId}/training-programs/${trainingProgramId}/workouts/add`}
        />
        <StyledSearchBar
          placeholder={t("Search") + "..."}
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
        <StyledDropdown
          menu={{
            items: sortByOptions,
            onClick: handleSortClick,
            selectedKeys: [currentSortBy],
          }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ width: 150, direction: "rtl" }}
        >
          <StyledFilterButton type="text" icon={<StyledSortIcon />}>
            {t("Sort")}
          </StyledFilterButton>
        </StyledDropdown>
      </StyledFiltersBar>
    </>
  );
};
